import React from 'react';
import { Modal, ModalBody, ModalHeader } from 'reactstrap';
import './delete-discussion-modal.scss';
import { Discussion } from '../models/discussion';
import { Member } from '../models/member';
import VoteTable from '../vote-component/VoteTable';
import ButtonLoader from '../../components/Layout/Buttons/ButtonLoader';
import { Poster } from '../models/poster';
import { ClubhouseMemberDiscussionsVotingPermission } from '../models/clubhouse-member-permissions';

interface DeleteDiscussionModalProps {
    discussion: Discussion;
    currentMemberId: number;
    setIsVisible(value: boolean): void;
    isVisible: boolean;
    pinnedDiscussions: string[];
    members: Array<Member>;
    startVoting(discussionId: number): void;
    posterVote(discussionId: number, vote: boolean): void;
    isUserAdmin: boolean;
    doesMemberHasViewOnlyAccessToPoc: boolean;
    customDiscussionsName?: string;
}

const MEMBER_ACTIVITY_PERIOD = 90;

enum VoteValue {
    keep = 'keep',
    remove = 'remove',
}

const DeleteDiscussionModal = (props: DeleteDiscussionModalProps) => {
    const hidePopup = () => props.setIsVisible(!props.isVisible);

    const isPinnedDiscussion = (): boolean =>
        props.pinnedDiscussions.some((d) => d === props.discussion.name);

    const isActivePoster = (lastMessageDate: Date): boolean => {
        let minLastActivityDate = new Date(
            props.discussion.voting?.dateInitiated || new Date()
        );
        minLastActivityDate.setDate(
            minLastActivityDate.getDate() - MEMBER_ACTIVITY_PERIOD
        );
        return new Date(lastMessageDate) > minLastActivityDate;
    };

    const hasPosterRightToVote = (poster: any) =>
        isActivePoster(poster.lastPostedDate) || poster.vote !== null;

    const saveVote = (vote: string) => {
        switch (vote) {
            case VoteValue.keep: {
                props.posterVote(props.discussion.id, false);
                return;
            }
            case VoteValue.remove: {
                props.posterVote(props.discussion.id, true);
                return;
            }
        }
    };

    const votingStarted = !!props.discussion.voting?.dateInitiated;

    let startedByName = '';
    if (props.discussion.voting?.memberId) {
        const starter = props.discussion.posters.find(
            (p: any) => p.id === props.discussion.voting.memberId
        );
        startedByName = starter ? starter.fullName : '';
    }

    const getPostersWithVotingPermission = () =>
        props.discussion.posters.filter((poster: Poster) => {
            const member = props.members.find(
                (m: Member) => m.id === poster.id
            );
            return (
                !member ||
                !member.permissions ||
                member.permissions.discussionsVotingPermission !==
                    ClubhouseMemberDiscussionsVotingPermission.NotAllowed
            );
        });

    return (
        <Modal
            className="discussion-hr-popup"
            isOpen={props.isVisible}
            toggle={hidePopup}>
            <ModalHeader toggle={hidePopup}></ModalHeader>

            <ModalBody className="delete-discussion-popup-body">
                <div className="delete-discussion-content-wrapper">
                    <div className="delete-discussion-content">
                        <div className="delete-discussion-content__icon-wrapper">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="56"
                                height="56"
                                viewBox="0 0 56 56">
                                <circle cx="28" cy="28" r="28" fill="#e63946" />
                                <line
                                    x1="18"
                                    y1="18"
                                    x2="38"
                                    y2="38"
                                    stroke="#fff"
                                    strokeWidth="4"
                                    strokeLinecap="round"
                                />
                                <line
                                    x1="38"
                                    y1="18"
                                    x2="18"
                                    y2="38"
                                    stroke="#fff"
                                    strokeWidth="4"
                                    strokeLinecap="round"
                                />
                            </svg>
                        </div>
                        <p className="delete-discussion-content__title">
                            Delete {props.customDiscussionsName || 'Thread'}
                        </p>
                        <p className="delete-discussion-content__text">
                            {votingStarted && startedByName ? (
                                <span>
                                    {startedByName} has initiated a vote to
                                    permanently delete this{' '}
                                    {props.customDiscussionsName
                                        ? props.customDiscussionsName.toLowerCase()
                                        : 'thread'}
                                    .
                                </span>
                            ) : (
                                <span>
                                    Start a vote to permanently delete this{' '}
                                    {props.customDiscussionsName
                                        ? props.customDiscussionsName.toLowerCase()
                                        : 'thread'}
                                    .
                                </span>
                            )}
                            {getPostersWithVotingPermission().length > 1 && (
                                <>
                                    {' '}
                                    Both participants must agree to complete the
                                    deletion.
                                </>
                            )}
                        </p>
                    </div>
                </div>
                {props.discussion.isRemovable &&
                    !isPinnedDiscussion() &&
                    !props.doesMemberHasViewOnlyAccessToPoc &&
                    votingStarted && (
                        <div className="vote-component-app">
                            <VoteTable
                                posters={getPostersWithVotingPermission()}
                                isVoteSaving={props.discussion.isVoteSaving}
                                currentMemberId={props.currentMemberId}
                                hasPosterRightToVote={hasPosterRightToVote}
                                saveVote={saveVote}
                                VoteValue={VoteValue}
                            />
                        </div>
                    )}
            </ModalBody>
            {props.discussion.isRemovable &&
                !isPinnedDiscussion() &&
                !props.doesMemberHasViewOnlyAccessToPoc &&
                !votingStarted && (
                    <div className="modal-footer custom-delete-footer">
                        <button
                            type="button"
                            className="btn btn-outline-secondary min-width-btn"
                            onClick={hidePopup}>
                            Cancel
                        </button>
                        <ButtonLoader
                            buttonText={`Initiate Vote to Delete ${
                                props.customDiscussionsName || 'Thread'
                            }`}
                            loaderButtonText={`Initiate Vote to Delete ${
                                props.customDiscussionsName || 'Thread'
                            }`}
                            onClick={() =>
                                props.startVoting(props.discussion.id)
                            }
                            disabled={
                                props.isUserAdmin ||
                                props.discussion.isVotingInitializing
                            }
                            isLoading={props.discussion.isVotingInitializing}
                            className={
                                props.discussion.isVotingInitializing
                                    ? 'btn btn-primary loader-submit'
                                    : 'btn btn-primary'
                            }
                        />
                    </div>
                )}
        </Modal>
    );
};

export default DeleteDiscussionModal;
